/**
 * What the agent page shows (slice 1e spec §2): the status line, the week's
 * numbers, the parts of IDENTITY.md and SOUL.md it quotes, the links into
 * Memory and Settings, and how the AI reads. Pure, so it's tested on its own
 * (`agent-view.test.ts`).
 */
import type { StatusTone } from "@crew/ui/components/status";
import type { AiConnection, Me } from "../../app/me";
import { relativeTime } from "../memory/memory-view";
import { accountState } from "../settings/ai-view";
import { modelLabel, PROVIDERS, type Catalog } from "../setup/onboarding";

type Agent = Me["agents"][number];
type Ai = NonNullable<Me["computer"]>["ai"];

/** `GET /api/agents/:slug` (spec §3). */
export type AgentPage = {
  agent: { id: string; slug: string; name: string; glyph: string; tint: string; role: string };
  week: { replies: number; chats: number; computer_ms: number; last_reply_at: string | null };
  /** Null before checkout; `last_wake_at` null before the first wake. */
  computer: { last_wake_at: string | null } | null;
};

export type StatusLine = { tone: StatusTone; text: string };

/**
 * The header's status: working on a reply while it has a running turn, else
 * the computer, in the sidebar's words; a computer that can't be asked until
 * the AI is connected says so.
 */
export function statusLine(agent: Pick<Agent, "status">, computer: Me["computer"]): StatusLine {
  if (agent.status === "working") return { tone: "working", text: "Working on a reply" };
  if (!computer) return { tone: "asleep", text: "No computer yet" };
  switch (computer.status) {
    case "setting_up":
      return { tone: "working", text: "Setting up" };
    case "waiting":
      return { tone: "queued", text: "Waiting for a computer" };
    case "waking":
      // An update wakes the computer first: it reads as the update (Devin on #405).
      if (computer.ai.updating) return { tone: "working", text: "Updating" };
      return { tone: "working", text: "Waking up" };
    case "error":
      return { tone: "error", text: "Computer needs attention" };
    case "released":
      return { tone: "asleep", text: "Computer released" };
  }
  switch (computer.ai.status) {
    case "connected":
      break;
    case "connecting":
      // Slice 1j §6.5: an update keeps the AI it thinks with; asks queue.
      if (computer.ai.updating) return { tone: "working", text: "Updating" };
      return { tone: "working", text: "Connecting its AI" };
    case "needs_reconnect":
      return { tone: "error", text: "AI needs reconnecting" };
    default:
      return { tone: "waiting", text: "Waiting for its AI to be connected" };
  }
  return computer.status === "ready"
    ? { tone: "done", text: "Awake · ready to help" }
    : { tone: "asleep", text: "Asleep · wakes when someone asks" };
}

/** Computer time as the page writes it: "0m", "<1m", "24m", "1h 5m". */
export function duration(ms: number): string {
  if (ms <= 0) return "0m";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "<1m";
  if (minutes < 60) return `${minutes}m`;
  const rest = minutes % 60;
  return rest ? `${Math.floor(minutes / 60)}h ${rest}m` : `${minutes / 60}h`;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

export type WeekView =
  | { empty: true }
  | {
      empty: false;
      stats: Array<{ value: string; label: string }>;
      /** "Last reply 2 hours ago" */
      last: string | null;
    };

/** This week: replies, chats, computer time and the last reply, or nothing yet. */
export function weekView(week: AgentPage["week"], timeZone: string, now = Date.now()): WeekView {
  if (week.replies === 0) return { empty: true };
  const when = week.last_reply_at ? relativeTime(week.last_reply_at, timeZone, now) : "";
  return {
    empty: false,
    stats: [
      { value: String(week.replies), label: plural(week.replies, "Reply", "Replies") },
      { value: String(week.chats), label: plural(week.chats, "Chat", "Chats") },
      { value: duration(week.computer_ms), label: "On computer" },
    ],
    last: when ? `Last reply ${when}` : null,
  };
}

/** IDENTITY.md as About shows it: without its own title (the section has one); null when empty. */
export function identityBody(content: string): string | null {
  const body = content.replace(/^\s*#\s[^\n]*\n?/, "").trim();
  return body || null;
}

/** SOUL.md's first paragraph that isn't a heading, as one line; null when there's none. */
export function firstParagraph(content: string): string | null {
  for (const block of content.split(/\n\s*\n/)) {
    const text = block.trim();
    if (text && !text.startsWith("#")) return text.replace(/\s*\n\s*/g, " ");
  }
  return null;
}

/** A file on the Memory page: an agent's (`?agent=main&file=SOUL.md`) or the workspace's. */
export function memoryLink(file: string, agentSlug?: string): string {
  const q = new URLSearchParams();
  if (agentSlug) q.set("agent", agentSlug);
  q.set("file", file);
  return `/memory?${q.toString()}`;
}

/**
 * Remembers' workspace files, by the Memory page's names and when they load
 * (slice 1d §3.1): Context every run, beside the agent's Notes; Voice and
 * Crew when needed, before writing for the workspace or when the work
 * involves someone else on the crew.
 */
export const EVERY_RUN_FILE = { name: "USER.md", title: "Context" } as const;
export const WHEN_NEEDED_FILES = [
  { name: "VOICE.md", title: "Voice" },
  { name: "CREW.md", title: "Crew" },
] as const;

export type AccountLine = { id: string; label: string; provider: string; status: StatusLine };

export type ThinksWith =
  | { connected: false }
  | { connected: true; model: string | null; fallbacks: string[]; accounts: AccountLine[] };

/**
 * The models it thinks with and the accounts behind them. An account that
 * needs reconnecting reads in the warning tone (spec §2.6), not Settings' red.
 */
export function thinksWith(
  ai: Ai | undefined,
  catalog: Catalog | undefined,
  timeZone: string,
  now = Date.now(),
): ThinksWith {
  // An update (slice 1j §6.5) keeps the model it thinks with now.
  const working = ai?.status === "connected" || ai?.status === "needs_reconnect" || ai?.updating;
  if (!ai || !working) return { connected: false };
  const accounts = [...(ai.connections ?? [])]
    .filter((c) => c.status !== "failed")
    .sort((a, b) => a.position - b.position)
    .map((c) => accountLine(c, ai, timeZone, now));
  return {
    connected: true,
    model: modelLabel(catalog, ai.model),
    fallbacks: (ai.fallbacks ?? []).map((m) => modelLabel(catalog, m) ?? m),
    accounts,
  };
}

function accountLine(c: AiConnection, ai: Ai, timeZone: string, now: number): AccountLine {
  const state = accountState(c, ai, now, timeZone);
  const tone = state.key === "needs_reconnect" ? "waiting" : state.tone;
  return {
    id: c.id,
    label: c.label,
    provider: PROVIDERS[c.provider]?.name ?? c.provider,
    status: { tone, text: state.text },
  };
}

/** "Last woke 2 hours ago", from `GET /api/agents/:slug`; null before it ever has. */
export function lastWoke(at: string | null | undefined, timeZone: string, now = Date.now()) {
  if (!at) return null;
  const when = relativeTime(at, timeZone, now);
  return when ? `Last woke ${when}` : null;
}
